import { useEffect, useMemo, useState } from 'react'
import { extractApiErrorMessage } from '../../../../utils/apiError'
import {
  downloadUserAnalyticsExport,
  fetchUserAnalyticsDrilldown,
  fetchUserAnalyticsFilters,
  fetchUserAnalyticsInsights,
} from '../../api/analyticsApi'

function todayIso() {
  return new Date().toISOString().slice(0, 10)
}

function daysAgoIso(days) {
  const current = new Date()
  current.setDate(current.getDate() - Math.max(0, Number(days || 0)))
  return current.toISOString().slice(0, 10)
}

function toDropdownOptions(items = [], emptyLabel = 'All') {
  return [{ value: '', label: emptyLabel }, ...items.map((item) => ({ value: String(item.id), label: item.name }))]
}

export function useUserAnalytics() {
  const [userId, setUserId] = useState('')
  const [departmentId, setDepartmentId] = useState('')
  const [reportingManagerId, setReportingManagerId] = useState('')
  const [projectId, setProjectId] = useState('')
  const [presetDays, setPresetDays] = useState('30')
  const [dateFrom, setDateFrom] = useState(daysAgoIso(29))
  const [dateTo, setDateTo] = useState(todayIso())
  const [users, setUsers] = useState([])
  const [departments, setDepartments] = useState([])
  const [projects, setProjects] = useState([])
  const [reportingManagers, setReportingManagers] = useState([])
  const [datePresets, setDatePresets] = useState([])
  const [insights, setInsights] = useState(null)
  const [isLoadingFilters, setIsLoadingFilters] = useState(true)
  const [isLoadingInsights, setIsLoadingInsights] = useState(true)
  const [error, setError] = useState('')

  // Drill-down Drawer State
  const [isDetailsOpen, setIsDetailsOpen] = useState(false)
  const [drillTitle, setDrillTitle] = useState('Productivity Activity')
  const [drillLevel, setDrillLevel] = useState('productivity')
  const [drillPeriodFrom, setDrillPeriodFrom] = useState('')
  const [drillPeriodTo, setDrillPeriodTo] = useState('')
  const [drilldown, setDrilldown] = useState({ level: 'productivity', columns: [], rows: [] })
  const [isLoadingDrilldown, setIsLoadingDrilldown] = useState(false)
  const [drilldownError, setDrilldownError] = useState('')
  const [isExporting, setIsExporting] = useState(false)

  // Chart View / Granularity / Metric Toggles
  const [trendMetric, setTrendMetric] = useState('hours') // 'hours' | 'percent' | 'efficiency' | 'tasks'
  const [trendGranularity, setTrendGranularity] = useState('weekly') // 'daily' | 'weekly' | 'monthly'
  const [trendView, setTrendView] = useState('chart') // 'chart' | 'table'

  useEffect(() => {
    let active = true
    async function loadFilters() {
      setIsLoadingFilters(true)
      try {
        const payload = await fetchUserAnalyticsFilters()
        if (!active) return
        setUsers(Array.isArray(payload?.users) ? payload.users : [])
        setDepartments(Array.isArray(payload?.departments) ? payload.departments : [])
        setProjects(Array.isArray(payload?.projects) ? payload.projects : [])
        setReportingManagers(Array.isArray(payload?.reportingManagers) ? payload.reportingManagers : [])
        setDatePresets(Array.isArray(payload?.datePresets) ? payload.datePresets : [])
      } catch (requestError) {
        if (!active) return
        setUsers([])
        setDepartments([])
        setProjects([])
        setReportingManagers([])
        setDatePresets([])
        setError(extractApiErrorMessage(requestError, 'Failed to load analytics filters.'))
      } finally {
        if (active) setIsLoadingFilters(false)
      }
    }
    loadFilters()
    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    if (!userId) return
    const isValid = users.some((item) => String(item.id) === String(userId))
    if (!isValid) {
      setUserId('')
    }
  }, [userId, users])

  useEffect(() => {
    if (!departmentId) return
    const isValid = departments.some((item) => String(item.id) === String(departmentId))
    if (!isValid) {
      setDepartmentId('')
    }
  }, [departmentId, departments])

  useEffect(() => {
    let active = true
    async function loadInsights() {
      setIsLoadingInsights(true)
      setError('')
      try {
        const payload = await fetchUserAnalyticsInsights({
          userId: userId || undefined,
          departmentId: departmentId || undefined,
          reportingManagerId: reportingManagerId || undefined,
          projectId: projectId || undefined,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
        })
        if (!active) return
        setInsights(payload || {})
      } catch (requestError) {
        if (!active) return
        setInsights(null)
        setError(extractApiErrorMessage(requestError, 'Failed to load user analytics insights.'))
      } finally {
        if (active) setIsLoadingInsights(false)
      }
    }
    loadInsights()
    return () => {
      active = false
    }
  }, [userId, departmentId, reportingManagerId, projectId, dateFrom, dateTo])

  function applyPreset(daysValue) {
    const parsed = Number(daysValue || 30)
    const normalizedDays = Number.isFinite(parsed) && parsed > 0 ? parsed : 30
    setPresetDays(String(normalizedDays))
    setDateTo(todayIso())
    setDateFrom(daysAgoIso(normalizedDays - 1))
  }

  async function loadDrilldown(level = drillLevel, periodFrom = drillPeriodFrom, periodTo = drillPeriodTo) {
    setIsLoadingDrilldown(true)
    setDrilldownError('')
    try {
      const payload = await fetchUserAnalyticsDrilldown({
        userId: userId || undefined,
        departmentId: departmentId || undefined,
        reportingManagerId: reportingManagerId || undefined,
        projectId: projectId || undefined,
        dateFrom: periodFrom || dateFrom || undefined,
        dateTo: periodTo || dateTo || undefined,
        level,
      })
      setDrilldown(payload || { level, columns: [], rows: [] })
    } catch (requestError) {
      setDrilldown({ level, columns: [], rows: [] })
      setDrilldownError(extractApiErrorMessage(requestError, 'Failed to load drill-down records.'))
    } finally {
      setIsLoadingDrilldown(false)
    }
  }

  function openDetails(title, level = 'productivity', periodFrom = '', periodTo = '') {
    setDrillTitle(title)
    setDrillLevel(level)
    setDrillPeriodFrom(periodFrom)
    setDrillPeriodTo(periodTo)
    setIsDetailsOpen(true)
    loadDrilldown(level, periodFrom, periodTo)
  }

  function closeDetails() {
    setIsDetailsOpen(false)
    setDrilldownError('')
  }

  async function exportCurrentDrilldown(format = 'csv') {
    setIsExporting(true)
    try {
      const blob = await downloadUserAnalyticsExport({
        userId: userId || undefined,
        departmentId: departmentId || undefined,
        reportingManagerId: reportingManagerId || undefined,
        projectId: projectId || undefined,
        dateFrom: drillPeriodFrom || dateFrom || undefined,
        dateTo: drillPeriodTo || dateTo || undefined,
        level: drillLevel,
        format,
      })
      const url = window.URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = `user_analytics_${drillLevel}.${format}`
      anchor.click()
      window.URL.revokeObjectURL(url)
    } catch (requestError) {
      setDrilldownError(extractApiErrorMessage(requestError, 'Failed to export user analytics.'))
    } finally {
      setIsExporting(false)
    }
  }

  const userOptions = useMemo(() => toDropdownOptions(users, 'All Users'), [users])
  const departmentOptions = useMemo(() => toDropdownOptions(departments, 'All Departments'), [departments])
  const reportingManagerOptions = useMemo(() => toDropdownOptions(reportingManagers, 'All Managers'), [reportingManagers])
  const projectOptions = useMemo(() => toDropdownOptions(projects, 'All Projects'), [projects])
  const presetOptions = useMemo(
    () =>
      (datePresets.length ? datePresets : [{ label: 'Last 30 Days', days: 30 }]).map((item) => ({
        value: String(item.days),
        label: item.label,
      })),
    [datePresets],
  )

  return {
    state: {
      userId,
      departmentId,
      reportingManagerId,
      projectId,
      presetDays,
      dateFrom,
      dateTo,
      users,
      departments,
      projects,
      reportingManagers,
      userOptions,
      departmentOptions,
      reportingManagerOptions,
      projectOptions,
      presetOptions,
      insights,
      isLoading: isLoadingFilters || isLoadingInsights,
      isLoadingFilters,
      isLoadingInsights,
      error,

      // Drilldown state
      isDetailsOpen,
      drillTitle,
      drillLevel,
      drillPeriodFrom,
      drillPeriodTo,
      drilldown,
      isLoadingDrilldown,
      drilldownError,
      isExporting,

      // Toggles
      trendMetric,
      trendGranularity,
      trendView,
    },
    actions: {
      setUserId,
      setDepartmentId,
      setReportingManagerId,
      setProjectId,
      setDateFrom,
      setDateTo,
      applyPreset,
      setTrendMetric,
      setTrendGranularity,
      setTrendView,
      openDetails,
      openProductivity: (pFrom = '', pTo = '') => openDetails('Productivity Activity Trail', 'productivity', pFrom, pTo),
      openAllocation: (pFrom = '', pTo = '') => openDetails('Project Allocation Breakdown', 'allocation', pFrom, pTo),
      openTeamHierarchy: () => openDetails('Team Reporting Hierarchy', 'team', '', ''),
      openLedger: (pFrom = '', pTo = '') => openDetails('Operational Ledger Audit', 'ledger', pFrom, pTo),
      openCostBreakdown: (pFrom = '', pTo = '') => openDetails('User Cost Breakdown', 'cost', pFrom, pTo),
      openSessions: (pFrom = '', pTo = '') => openDetails('Agent Session History', 'session', pFrom, pTo),
      openActivity: (pFrom = '', pTo = '') => openDetails('Application & Activity History', 'activity', pFrom, pTo),
      closeDetails,
      exportCurrentDrilldown,
      resetFilters: () => {
        setUserId('')
        setDepartmentId('')
        setReportingManagerId('')
        setProjectId('')
        applyPreset('30')
        if (isDetailsOpen) {
          loadDrilldown(drillLevel, '', '')
        }
      },
    },
  }
}
